// Hidden admin panel (URL-only /admin) for ken@futureengine.io — manages the tester
// allowlist AND shows per-tester usage (cohort-health header + counts + recency). This
// client gate is UX; the REAL lock is firestore.rules (isAdmin()). Counts are client-trusted
// → labeled "approximate". The allowlist PAINTS immediately; usage streams in (a usage-load
// failure shows "—" and never blocks the panel).
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ADMIN_EMAIL, isValidEmail, listAllowlist, addToAllowlist, removeFromAllowlist,
  type AllowlistEntry,
} from "@/lib/adminAllowlist";
import { listUsage, type UsageEntry } from "@/lib/usageCounter";
import {
  listPendingWaitlist, approveWaitlist, dismissWaitlist, undismissWaitlist, type WaitlistEntry,
} from "@/lib/waitlist";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Human "2d ago" / "3h ago" / "5m ago" / "just now" / "never" from an ISO string. */
function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return "never";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** Coarse "2d ago" recency from an ISO stamp (waitlist requestedAt). */
function relativeAge(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d ago`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours}h ago`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m ago`;
}

export default function Admin(): JSX.Element | null {
  const { user, corp, loading } = useAuth();
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [usage, setUsage] = useState<UsageEntry[] | null>(null); // null = loading; [] = loaded-empty
  const [usageError, setUsageError] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<WaitlistEntry[] | null>(null); // null = loading → badge shows "—"
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [lastDismissed, setLastDismissed] = useState<string | null>(null);

  const isAdmin = corp && user?.email?.toLowerCase() === ADMIN_EMAIL;

  const refresh = async () => {
    setListLoading(true);
    setError(null);
    setUsageError(false);
    setUsage(null);
    // Kick BOTH off in parallel; paint the allowlist as soon as it lands, stream usage in.
    const usageP = listUsage();
    try {
      setEntries(await listAllowlist());
    } catch {
      setError("Couldn't load the allowlist.");
    } finally {
      setListLoading(false);
    }
    try {
      setUsage(await usageP);
    } catch {
      setUsage(null);
      setUsageError(true);
    }
  };

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin]);

  const loadPending = async () => {
    setPendingError(null);
    try {
      setPending(await listPendingWaitlist());
    } catch {
      setPending([]);
      setPendingError("Couldn't load pending requests.");
    }
  };

  useEffect(() => {
    if (isAdmin) void loadPending();
  }, [isAdmin]);

  if (loading) return null;
  if (!isAdmin) return <Navigate to="/create" replace />;

  const onApprove = async (email: string) => {
    setBusy(true);
    setPendingError(null);
    try {
      await approveWaitlist(email);
      await loadPending();
      await refresh();
    } catch {
      setPendingError(`Couldn't approve ${email} — try again.`);
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = async (email: string) => {
    if (!window.confirm(`Dismiss ${email}?`)) return;
    setBusy(true);
    setPendingError(null);
    try {
      await dismissWaitlist(email);
      setLastDismissed(email);
      await loadPending();
    } catch {
      setPendingError(`Couldn't dismiss ${email}.`);
    } finally {
      setBusy(false);
    }
  };

  const onUndo = async (email: string) => {
    setBusy(true);
    try {
      await undismissWaitlist(email);
      setLastDismissed(null);
      await loadPending();
    } catch {
      setPendingError(`Couldn't undo ${email}.`);
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      await addToAllowlist(input);
      setInput("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't add that email.");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (email: string) => {
    setBusy(true);
    setError(null);
    try {
      await removeFromAllowlist(email);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't remove that email.");
    } finally {
      setBusy(false);
    }
  };

  // Join allowlist ⋈ usage (by lowercased email), sort most-recently-active first (never → bottom).
  const usageByEmail = new Map((usage ?? []).map((u) => [u.email.toLowerCase(), u]));
  const rows = entries
    .map((entry) => ({ entry, usage: usageByEmail.get(entry.email.toLowerCase()) ?? null }))
    .sort((a, b) => (b.usage?.lastUsedAt ?? "").localeCompare(a.usage?.lastUsedAt ?? ""));

  // Cohort-health rollup (testers = allowlist size; the rest sum the loaded usage array).
  const now = Date.now();
  const list = usage ?? [];
  const activeThisWeek = list.filter((u) => u.lastUsedAt && now - Date.parse(u.lastUsedAt) < WEEK_MS).length;
  const sumRoasts = list.reduce((s, u) => s + u.roast, 0);
  const sumToasts = list.reduce((s, u) => s + u.toast, 0);
  const sumExports = list.reduce((s, u) => s + u.exports, 0);

  // The app locks html/body to `height:100%; overflow:hidden` (globals.css) because every
  // other screen is a full-bleed non-scrolling view. /admin grows past the viewport (pending
  // + allowlist + usage rows), so it needs its OWN viewport-height scroll region — a plain
  // tall <main> would just be clipped by the body's overflow:hidden. h-[100dvh] (not 100vh) so
  // mobile browser chrome doesn't hide the bottom rows.
  return (
    <div className="h-[100dvh] overflow-y-auto">
    <main className="mx-auto max-w-xl px-6 py-10 text-white">
      <section className="mb-8">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Pending requests</h2>
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/70">
            {pending === null ? "—" : pending.length}
          </span>
        </div>
        {pendingError && <p className="mt-2 text-sm text-red-400">{pendingError}</p>}
        {lastDismissed && (
          <p className="mt-2 text-xs text-white/60">
            Dismissed {lastDismissed}.{" "}
            <button className="underline" onClick={() => void onUndo(lastDismissed)}>Undo</button>
          </p>
        )}
        <ul className="mt-3 divide-y divide-white/10">
          {pending === null ? (
            <li className="py-3 text-sm text-white/50">Loading…</li>
          ) : pending.length === 0 ? (
            <li className="py-3 text-sm text-white/50">No pending requests.</li>
          ) : (
            pending.map((p) => (
              <li key={p.email} className="flex items-center justify-between py-3">
                <span className="text-sm">
                  {p.displayName ? `${p.displayName} · ` : ""}{p.email}
                  <span className="ml-2 text-xs text-white/40">{relativeAge(p.requestedAt)}</span>
                </span>
                <span className="flex gap-2">
                  <Button size="sm" disabled={busy} onClick={() => void onApprove(p.email)}>Approve</Button>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => void onDismiss(p.email)}>Dismiss</Button>
                </span>
              </li>
            ))
          )}
        </ul>
      </section>

      <h1 className="text-2xl font-bold">Allowlist</h1>
      <p data-testid="cohort-header" className="mt-1 text-sm text-white/70">
        {entries.length} tester{entries.length === 1 ? "" : "s"} ·{" "}
        {usageError
          ? "—"
          : `${activeThisWeek} active this week · ${sumRoasts} roasts · ${sumToasts} toasts · ${sumExports} exports`}
      </p>
      <p className="mt-0.5 text-xs text-white/40">Counts are approximate.</p>

      <div className="mt-6 flex gap-2">
        <Input
          placeholder="email to add"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && isValidEmail(input) && !busy) void onAdd(); }}
        />
        <Button onClick={() => void onAdd()} disabled={busy || !isValidEmail(input)}>Add</Button>
      </div>

      {error && <p className="mt-3 text-sm text-red-400">{error}</p>}

      <ul className="mt-6 divide-y divide-white/10">
        {listLoading ? (
          <li className="py-3 text-sm text-white/50">Loading…</li>
        ) : rows.length === 0 ? (
          <li className="py-3 text-sm text-white/50">No testers yet.</li>
        ) : (
          rows.map(({ entry, usage: u }) => (
            <li key={entry.email} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span data-testid="tester-email" className="truncate text-sm">{entry.email}</span>
                  {entry.addedAt && <span className="text-xs text-white/40">{entry.addedAt.slice(0, 10)}</span>}
                </div>
                <div className="mt-0.5 text-xs">
                  {usageError ? (
                    <span className="text-white/40">—</span>
                  ) : usage === null ? (
                    <span className="text-white/40">…</span>
                  ) : u && u.total > 0 ? (
                    <span className="text-white/60">
                      {u.total} · roast {u.roast} · toast {u.toast} · exports {u.exports}
                      <span className="ml-2 text-white/40">{relativeTime(u.lastUsedAt)}</span>
                    </span>
                  ) : (
                    <span className="text-amber-400">never used</span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove ${entry.email}`}
                disabled={busy || entry.email === ADMIN_EMAIL}
                onClick={() => void onRemove(entry.email)}
              >
                ✕
              </Button>
            </li>
          ))
        )}
      </ul>
    </main>
    </div>
  );
}
